import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API, authHeaders, errText } from '../api';
import Layout from './Layout';
import { ui } from '../ui';
import { colors } from '../theme';
import DataTable from '../components/DataTable';

const OPTION_KEYS = ['option_a', 'option_b', 'option_c', 'option_d'];
const OPTION_LABELS = ['А', 'Б', 'В', 'Г'];

const emptyForm = { topic: '', text: '', option_a: '', option_b: '', option_c: '', option_d: '', sort_order: 0 };

export default function QuizQuestions() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);

  const fetchItems = useCallback(() => {
    axios.get(`${API}/admin/quiz-questions`, { headers: authHeaders() }).then(r => setItems(r.data));
  }, []);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const formComplete = (f) => f.topic.trim() && f.text.trim() && OPTION_KEYS.every(k => f[k].trim());

  const create = async () => {
    if (!formComplete(form)) return;
    setError('');
    setCreating(true);
    try {
      await axios.post(`${API}/admin/quiz-questions`, {
        ...form, sort_order: Number(form.sort_order) || items.length,
      }, { headers: authHeaders() });
      setForm({ ...emptyForm, sort_order: items.length + 1 });
      fetchItems();
    } catch (e) {
      setError(errText(e, 'Не удалось создать вопрос'));
    }
    setCreating(false);
  };

  const toggleActive = async (q) => {
    await axios.patch(`${API}/admin/quiz-questions/${q.id}`, { is_active: !q.is_active }, { headers: authHeaders() });
    fetchItems();
  };

  const startEdit = (q) => {
    setEditingId(q.id);
    setEditForm({
      topic: q.topic, text: q.text, sort_order: q.sort_order,
      option_a: q.option_a, option_b: q.option_b, option_c: q.option_c, option_d: q.option_d,
    });
  };
  const cancelEdit = () => setEditingId(null);

  const saveEdit = async (q) => {
    if (!formComplete(editForm)) return;
    await axios.patch(`${API}/admin/quiz-questions/${q.id}`, {
      ...editForm, sort_order: Number(editForm.sort_order) || 0,
    }, { headers: authHeaders() });
    setEditingId(null);
    fetchItems();
  };

  return (
    <Layout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h1 style={{ ...ui.title, margin: 0 }}>Открытым Текстом</h1>
        <button style={ui.btnSecondary} onClick={() => navigate('/quiz-stats')}>📊 Статистика по вопросам</button>
      </div>
      <p style={{ color: colors.textMuted, fontSize: 13, marginTop: -10, marginBottom: 20 }}>
        Вопросы для анкеты «Открытым Текстом» — 4 варианта ответа на каждый. Ответы пользователей
        конфиденциальны — ни здесь, ни в карточке анкеты выбранный вариант не показывается, только
        отметка «ответил / не ответил». Удалить вопрос нельзя (это стёрло бы и ответы пользователей
        на него) — только скрыть.
      </p>

      <div style={ui.card}>
        <div style={{ fontWeight: 600, marginBottom: 12 }}>Новый вопрос</div>
        <div style={ui.row}>
          <input style={{ ...ui.input, width: 160 }} placeholder="Тема" value={form.topic} onChange={e => setForm(f => ({ ...f, topic: e.target.value }))} />
          <input style={{ ...ui.input, flex: 1 }} placeholder="Текст вопроса" value={form.text} onChange={e => setForm(f => ({ ...f, text: e.target.value }))} />
          <input style={{ ...ui.input, width: 90 }} type="number" placeholder="Порядок" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))} />
        </div>
        <div style={{ ...ui.row, marginTop: 8 }}>
          {OPTION_KEYS.map((key, i) => (
            <input
              key={key} style={{ ...ui.input, flex: 1 }} placeholder={`Вариант ${OPTION_LABELS[i]}`}
              value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
            />
          ))}
        </div>
        <button style={{ ...ui.btn, marginTop: 12 }} onClick={create} disabled={creating}>{creating ? 'Создаём…' : 'Добавить'}</button>
        {error && <div style={ui.error}>{error}</div>}
      </div>

      <div style={ui.card}>
        <DataTable
          rows={items}
          rowKey={q => q.id}
          emptyText="Вопросов пока нет"
          columns={[
            {
              key: 'topic', label: 'Тема',
              render: q => editingId === q.id
                ? <input style={{ ...ui.input, width: 120 }} value={editForm.topic} onChange={e => setEditForm(f => ({ ...f, topic: e.target.value }))} />
                : q.topic,
            },
            {
              key: 'text', label: 'Вопрос',
              render: q => editingId === q.id ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 260 }}>
                  <input style={ui.input} value={editForm.text} onChange={e => setEditForm(f => ({ ...f, text: e.target.value }))} />
                  {OPTION_KEYS.map((key, i) => (
                    <input
                      key={key} style={ui.input} placeholder={`Вариант ${OPTION_LABELS[i]}`}
                      value={editForm[key]} onChange={e => setEditForm(f => ({ ...f, [key]: e.target.value }))}
                    />
                  ))}
                </div>
              ) : (
                <div>
                  <div>{q.text}</div>
                  <div style={{ color: colors.textMuted, fontSize: 12, marginTop: 4 }}>
                    {OPTION_LABELS.map((label, i) => `${label}: ${q[OPTION_KEYS[i]]}`).join('  ·  ')}
                  </div>
                </div>
              ),
            },
            {
              key: 'sort_order', label: 'Порядок',
              render: q => editingId === q.id
                ? <input style={{ ...ui.input, width: 70 }} type="number" value={editForm.sort_order} onChange={e => setEditForm(f => ({ ...f, sort_order: e.target.value }))} />
                : q.sort_order,
            },
            { key: 'answer_count', label: 'Ответы', render: q => q.answer_count },
            { key: 'is_active', label: 'Виден', sortValue: q => (q.is_active ? 1 : 0), render: q => (q.is_active ? 'Да' : 'Нет') },
            {
              key: 'actions', label: '', sortable: false,
              render: q => editingId === q.id ? (
                <>
                  <button style={ui.btn} onClick={() => saveEdit(q)}>Сохранить</button>{' '}
                  <button style={ui.btnSecondary} onClick={cancelEdit}>Отмена</button>
                </>
              ) : (
                <>
                  <button style={ui.btnSecondary} onClick={() => startEdit(q)}>Изменить</button>{' '}
                  <button style={ui.btnSecondary} onClick={() => toggleActive(q)}>{q.is_active ? 'Скрыть' : 'Показать'}</button>
                </>
              ),
            },
          ]}
        />
      </div>
    </Layout>
  );
}
